/**
 * Music Service Utilities
 * Handle URL redirection based on user's preferred music streaming service
 */
import { Music, Play, Headphones, Volume2, Radio, Disc, Speaker } from 'lucide-react';

export interface MusicServiceUrls {
  spotify?: string;
  appleMusicUrl?: string;
  amazonMusicUrl?: string;
  tidalUrl?: string;
  deezerUrl?: string;
  youtubeMusicUrl?: string;
  youtubeUrl?: string;
  qobuzUrl?: string;
}

export type MusicServiceId =
  | 'spotify'
  | 'appleMusic'
  | 'amazonMusic'
  | 'tidal'
  | 'deezer'
  | 'youtube'
  | 'youtubeMusic'
  | 'qobuz';

type MusicServiceInputId = MusicServiceId | string | null | undefined;

const MUSIC_SERVICE_NORMALIZATION_MAP: Record<string, MusicServiceId> = {
  spotify: 'spotify',
  'applemusic': 'appleMusic',
  'apple-music': 'appleMusic',
  'apple_music': 'appleMusic',
  apple: 'appleMusic',
  'amazonmusic': 'amazonMusic',
  'amazon-music': 'amazonMusic',
  'amazon_music': 'amazonMusic',
  amazon: 'amazonMusic',
  tidal: 'tidal',
  deezer: 'deezer',
  youtube: 'youtube',
  'youtube-music': 'youtubeMusic',
  'youtube_music': 'youtubeMusic',
  'youtubemusic': 'youtubeMusic',
  ytmusic: 'youtubeMusic',
  bandcamp: 'youtube', // default fallback since no native player exists yet
  qobuz: 'qobuz',
};

export function normalizeMusicServiceId(service: MusicServiceInputId): MusicServiceId {
  if (!service) return 'spotify';
  const normalizedKey = service.toString().trim().toLowerCase();
  return MUSIC_SERVICE_NORMALIZATION_MAP[normalizedKey] || 'spotify';
}

/**
 * Get the appropriate URL for the user's preferred music service
 */
export function getPreferredMusicUrl(
  urls: MusicServiceUrls, 
  preferredMusicService: MusicServiceId = 'spotify'
): string | null {
  const normalizedService = normalizeMusicServiceId(preferredMusicService);

  // Map service IDs to URL properties
  const serviceUrlMap: Record<MusicServiceId, keyof MusicServiceUrls> = {
    spotify: 'spotify',
    appleMusic: 'appleMusicUrl',
    amazonMusic: 'amazonMusicUrl',
    tidal: 'tidalUrl',
    deezer: 'deezerUrl',
    youtube: 'youtubeUrl',
    youtubeMusic: 'youtubeMusicUrl',
    qobuz: 'qobuzUrl'
  };

  // Get the preferred service URL
  const urlProperty = serviceUrlMap[normalizedService];
  const preferredUrl = urls[urlProperty];

  if (preferredUrl) {
    return preferredUrl;
  }

  // Fallback order: try other popular services
  const fallbackOrder: (keyof MusicServiceUrls)[] = [
    'spotify',
    'appleMusicUrl',
    'youtubeUrl',
    'youtubeMusicUrl',
    'amazonMusicUrl',
    'deezerUrl',
    'tidalUrl',
    'qobuzUrl'
  ];

  for (const serviceUrl of fallbackOrder) {
    if (urls[serviceUrl]) {
      return urls[serviceUrl];
    }
  }

  return null;
}

/**
 * Handle music link click with user preference
 */
export function handleMusicLinkClick(
  urls: MusicServiceUrls,
  preferredMusicService: MusicServiceId = 'spotify',
  openInNewTab: boolean = true
): void {
  const normalizedService = normalizeMusicServiceId(preferredMusicService);
  const url = getPreferredMusicUrl(urls, normalizedService);
  
  if (url) {
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
  }
}

/**
 * Get user's preferred music service from localStorage/API
 */
export function getUserPreferredMusicService(): MusicServiceId {
  if (typeof window === 'undefined') return 'spotify';
  
  try {
    const preferences = localStorage.getItem('userPreferences');
    if (preferences) {
      const parsed = JSON.parse(preferences);
      return normalizeMusicServiceId(parsed.preferredMusicService);
    }
  } catch (error) {
    console.warn('Error reading music service preference:', error);
  }
  
  const legacyValue = localStorage.getItem('preferredMusicService');
  if (legacyValue) {
    const normalized = normalizeMusicServiceId(legacyValue);
    // Persist normalized value in new structure for consistency
    setUserPreferredMusicService(normalized);
    return normalized;
  }

  return 'spotify';
}

/**
 * Set user's preferred music service in localStorage
 */
export function setUserPreferredMusicService(serviceId: MusicServiceId): void {
  if (typeof window === 'undefined') return;
  const normalized = normalizeMusicServiceId(serviceId);
  
  try {
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    preferences.preferredMusicService = normalized;
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    localStorage.setItem('preferredMusicService', normalized);
  } catch (error) {
    console.warn('Error saving music service preference:', error);
  }
}

/**
 * Get display name and icon for music services
 */
export function getMusicServiceInfo(serviceId: MusicServiceId) {
  const normalizedService = normalizeMusicServiceId(serviceId);
  const serviceInfo = {
    spotify: { name: 'Spotify', color: '#1DB954', icon: Music },
    appleMusic: { name: 'Apple Music', color: '#FA243C', icon: Play },
    amazonMusic: { name: 'Amazon Music', color: '#FF9900', icon: Volume2 },
    tidal: { name: 'Tidal', color: '#000000', icon: Headphones },
    deezer: { name: 'Deezer', color: '#FF6600', icon: Radio },
    youtube: { name: 'YouTube', color: '#FF0000', icon: Speaker },
    youtubeMusic: { name: 'YouTube Music', color: '#FF0000', icon: Disc },
    qobuz: { name: 'Qobuz', color: '#1A8FE3', icon: Headphones }
  };
  
  return serviceInfo[normalizedService] || serviceInfo.spotify;
}
